import React from "react";
import type { DepGraph } from "../../../../models";
import DepGanttStrip from "../../depCore/DepGanttStrip/DepGanttStrip";
import { type RowLayoutEntry } from "../../depCore/graphRows";

interface Props {
  rowLayout: RowLayoutEntry[];
  rowHeights: number[];
  calendar: string[];
  graph: DepGraph;
  stripScrollRef: React.RefObject<HTMLDivElement | null>;
  selectedId: string | null;
  /** Direct predecessors / successors of the selected row — their gantt rows
   *  tint light red / light yellow. */
  predIds?: Set<string>;
  succIds?: Set<string>;
  /** Select an activity by clicking its gantt row / bar (toggles like the table). */
  onSelect: (id: string) => void;
  drivingOnly: boolean;
  onDrivingOnlyChange: (v: boolean) => void;
  /** Hide the chart's ⚙ options gear — used when "Driving only" is surfaced as a
   *  toolbar pill instead (the dep modal). */
  hideSettings?: boolean;
}

const SequenceStripPane: React.FC<Props> = ({
  rowLayout,
  rowHeights,
  calendar,
  graph,
  stripScrollRef,
  selectedId,
  predIds,
  succIds,
  onSelect,
  drivingOnly,
  onDrivingOnlyChange,
  hideSettings = false,
}) => {
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const settingsRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!settingsOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!settingsRef.current?.contains(e.target as Node)) setSettingsOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [settingsOpen]);

  return (
    <div className="sequence-view-strip-pane">
      <DepGanttStrip
        rowLayout={rowLayout}
        rowHeights={rowHeights}
        calendar={calendar}
        graph={graph}
        bodyRef={stripScrollRef}
        selectedId={selectedId}
        predIds={predIds}
        succIds={succIds}
        onSelect={onSelect}
        drivingOnly={drivingOnly}
      />
      {!hideSettings && (
      <div className="sequence-view-strip-tools">
        <div className="sequence-view-settings" ref={settingsRef}>
          <button
            type="button"
            className="sequence-view-settings-btn"
            title="Chart options"
            aria-haspopup="dialog"
            aria-expanded={settingsOpen}
            onClick={() => setSettingsOpen((o) => !o)}
          >
            ⚙
          </button>
          {settingsOpen && (
            <div className="sequence-view-settings-pop">
              <label className="sequence-view-settings-row">
                <input
                  type="checkbox"
                  checked={drivingOnly}
                  onChange={(e) => onDrivingOnlyChange(e.target.checked)}
                />
                Driving lines only
              </label>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default SequenceStripPane;
